import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useBlocker, type Blocker } from 'react-router';

import { ApiError } from '@/api/http';
import {
  draftChanges,
  draftFieldsOf,
  fetchInterview,
  isReadable,
  saveInterviewDraft,
  type DraftFields,
  type InterviewDraft,
  type InterviewOut,
  type InterviewRead,
} from '@/api/interviews';
import { queryKeys } from '@/api/queryKeys';

import { draftFieldsOfForm, type FillingInForm } from './fillingInFields';

/**
 * **Feedback protection #4 — the draft is autosaved to the server, debounced, and never to
 * `localStorage` (ADR 0007).** This module is the one endpoint standing between a typed assessment and
 * its loss, and everything below is written for that one property.
 *
 * ⚠️ **`localStorage` is rejected by name in ADR 0007, and the reason is not tidiness.** The failure
 * this defends against is a closed tab on a machine the person never opens again — a laptop handed
 * back, a browser profile wiped, a session on somebody else's computer. Storage that lives on that
 * machine survives none of it, and it is exactly then that the feedback matters most. So there is no
 * `localStorage`, no `sessionStorage` and no cookie write anywhere on this screen; unsaved edits live
 * in **component state** (the form's own, plus the two refs below), and the only durable copy is the
 * one on the server. `autosave.test.tsx` asserts both halves — that both storages are empty after
 * typing, and that no module under `src/pages/Interview/` writes to either.
 *
 * ⚠️ **The debounce is a rate decision as well as a design one, and it is BUG-011's recorded revisit.**
 * `USER_THROTTLE_RATE = 300/min` — per user, across the **whole authenticated API** — shipped in
 * `827efcc` as *"revisitable when track 1F sets the real debounce"*. This is that revisit; the
 * arithmetic is on `AUTOSAVE_DEBOUNCE_MS` and `AUTOSAVE_MAX_WAIT_MS` below and both are pinned by
 * measurements, because a number chosen in a comment and checked nowhere is how the two drift apart.
 *
 * **What this hook is, in one line:** a trailing debounce over the form **with a maximum wait**, one
 * request in flight at a time, a bounded backoff behind it, and six states for the indicator to render
 * (§6.6). The maximum wait is not a refinement — without it the debounce never elapses while somebody
 * keeps typing, and this module's one property fails for the busiest reader it has (BUG-030). What it is
 * **not** is a queue that survives the tab: nothing here persists anything anywhere.
 */

/**
 * ⚠️ **The debounce, and the arithmetic that makes it safe against BUG-011's throttle.**
 *
 * Two seconds. It is long enough that a sentence being typed produces **one** save rather than one per
 * keystroke, and short enough that the window in which a typed assessment exists only in a browser is
 * measured in seconds — the two properties ADR 0007 is balancing, and §6.6 forbids resolving the
 * second one by turning the debounce into save-on-blur.
 *
 * **The bound this screen puts on the API, per user, per minute:**
 *
 * | What | Requests/min |
 * |---|---|
 * | saves in a two-second rhythm — the fastest the debounce allows, and the worst case | 30 |
 * | saves under typing that never pauses — `60_000 / AUTOSAVE_MAX_WAIT_MS` | 6 |
 * | reads on opening the screen — `/auth/me/` and the interview | 2 |
 * | **total, worst case** | **32** |
 *
 * The two save rows are **alternatives and never a sum**: the maximum wait only sends what the debounce
 * did not, and either one resets both timers, so the worst case is the larger of the two and not 36.
 *
 * 32 against `USER_THROTTLE_RATE = 300/min` is **11%**, i.e. inside one eighth of the budget — and one
 * eighth is what `autosave.test.tsx` pins, with a margin left for the rest of the app rather than a
 * bound tuned to the last request. The reads are counted at all because 300/min is a bound on the whole
 * authenticated API and not on this route (`PROD_DEPLOY_CHECKLIST.md`, TASK-082's entry says the same);
 * counting them as *per minute* is itself the conservative reading, since they happen once per screen
 * opened and the session read is shared and cached for the whole app. It was **three** reads until
 * backend TASK-107 put the candidate's name on the interview payload, and the third line is gone from
 * this table rather than left standing at a number nothing makes true.
 *
 * ⚠️ **Where each figure in that table comes from, stated exactly, because overstating it was one of
 * BUG-030's own findings.** The **2** is counted off the recorded requests
 * (`autosave.test.tsx`, *"the saves counted off the wire"*, which also asserts *which* two URLs they are).
 * The **30** is counted too, but by a different test — *"reaches the debounce ceiling in a two-second
 * rhythm"* — which types and pauses in that rhythm for a simulated minute and measures **29.3/min**, at
 * the ceiling and under it. The gap-free test bounds the rate **from above** and measures 5.95/min; it
 * cannot show the ceiling is tight, and the first version of this table claimed both figures were counted
 * when only the reads were. The **6** is what that same gap-free test counts.
 *
 * ⚠️ **30/min is a ceiling because *every* save is scheduled by one of these two timers — and it was
 * not, from 2026-07-30 until BUG-030 was fixed on 2026-08-03.** The queued edit (see the success path in
 * `attempt`) re-entered the engine the moment a save answered, without re-arming the debounce, so once a
 * keystroke had landed mid-flight the cadence was the **round-trip time**: ~1000 req/min at 60 ms, against
 * a 300/min throttle on the caller's whole authenticated API. The paragraph this replaced asserted 30/min
 * as *"a ceiling a human cannot beat"* while the engine beat it by more than an order of magnitude, and
 * the test that was supposed to hold it was arithmetic over these two constants. **The claim is therefore
 * stated as what enforces it rather than as a property of the number.**
 *
 * A trailing debounce fires once per pause of at least this long, and the only other thing that fires a
 * save is the maximum wait below — which is *longer*, so it cannot raise the cadence. The fastest
 * sustainable rate is one save per two seconds, reached only by somebody typing and pausing in perfect
 * two-second rhythm for a minute; ordinary use is far below it, a save per sentence. Two paths are
 * deliberately outside both timers and neither is a rate: the reader's own **Retry** (one request per
 * press, and a person pressing a button is not a runaway client) and the `online` event (one request per
 * reconnection, fired by the browser).
 *
 * ⚠️ **This interval does not make 300/min wrong in either direction, and the reason is recorded
 * rather than assumed.** Not too tight: even two tabs of the same interview (64/min with their reads)
 * plus the retry traffic below (at most 2/min once the backoff is capped) sit under a quarter of the
 * budget, so no interviewer meets a 429 by typing. Not too loose either — the bound still stops a runaway
 * client at five requests a second, which is the property BUG-011 shipped it for. So the throttle stays
 * as it is; had it needed changing, that would have been a Backlog item or a bug and never a silent edit.
 */
export const AUTOSAVE_DEBOUNCE_MS = 2_000;

/**
 * ⚠️ **The maximum wait — how far behind the screen the server is ever allowed to fall, and the half of
 * BUG-030 its first attempt left out.**
 *
 * A trailing debounce alone has a floor nobody notices until somebody hits it: **it never elapses while
 * the typing continues.** BUG-030's first fix made the queued mid-flight edit re-arm the debounce and
 * stopped there, which bounded the rate by bounding the saves to zero — measured in a live browser as
 * **193 characters over 22 s of gap-free typing → 0 PATCHes**, and then a closed tab lost all 193. That is
 * ADR 0007's named failure verbatim, on the one screen ADR 0007 exists for, and it is why this constant
 * exists: the debounce decides *when a pause is worth a save*, and this decides **how long a save may be
 * postponed by the absence of a pause**. Whichever comes first wins (see `arm`).
 *
 * **Ten seconds, and the two constants are chosen as a pair:**
 *
 * - `AUTOSAVE_MAX_WAIT_MS > AUTOSAVE_DEBOUNCE_MS` is the relationship that must hold. Below it the
 *   maximum wait would become the cadence and the ceiling in the table above would be
 *   `60_000 / AUTOSAVE_MAX_WAIT_MS` instead of `60_000 / AUTOSAVE_DEBOUNCE_MS` — a *higher* rate, not a
 *   lower one. It is not asserted over the constants here, because arithmetic over two constants is the
 *   defect BUG-030 was: what catches it is the rhythm test's **measured** ceiling, which the inversion
 *   pushes above 30/min.
 * - Five times the debounce is the ratio, so this timer is what fires for a **paragraph typed without
 *   stopping** and the debounce is what fires for everything else. Ten seconds of uninterrupted typing is
 *   of the order of a sentence: that is the most this screen can now lose to a closed tab, against the
 *   whole sitting the first attempt could lose and the one round trip the flooding engine could.
 * - It costs nothing against the throttle. Gap-free typing at this wait is **6/min**, a fifth of the
 *   debounce's own ceiling, so the worst case in the table above is unchanged at 30 saves a minute.
 * - It keeps the indicator honest. §6.6's indicator is the reassurance ADR 0007 offers **instead of** a
 *   Save button, and under the first attempt it showed *nothing at all* for a whole burst — no *Saving…*,
 *   no *Saved 14:32* — which is a screen silently claiming nothing while somebody works.
 */
export const AUTOSAVE_MAX_WAIT_MS = 10_000;

/**
 * ⚠️ **The retry delays — bounded in *delay*, unbounded in *attempts*, and each half is a promise.**
 *
 * §6.6's offline line says *"Your answers are kept in this tab until the connection returns"*, so the
 * attempts cannot be capped: a client that gave up after five would make that sentence false, which is
 * exactly the shape of BUG-017 (a retry promised by a string and performed by nothing). What **is**
 * capped is the delay, at the last entry, so a screen left open on a dead connection settles at one
 * request every 30 seconds — 2/min against 300/min.
 *
 * ⚠️ **The ladder carries only failures a *repetition* could resolve, and a 422 is not one** (BUG-068).
 * Unbounded attempts and *"Not saved — retrying"* are the same promise said twice, and a body the server
 * has refused on its content is refused identically for ever — so this ladder is the answer to a 5xx, a
 * 429 and a dropped connection, and the `refused` branch in `attempt` is the answer to a 422. Retrying
 * one is not persistence, it is BUG-017's shape with the string telling the truth about a request that
 * cannot succeed.
 *
 * ⚠️ **The first delay is what keeps a 429 from being amplified.** A throttled request is answered by
 * the same rule `queryClient.ts` states for queries (BUG-005, BUG-011): *"a throttle in particular must
 * not be amplified by the client that caused it"*. So a failure is **never** retried in the same tick
 * and the first attempt waits at least this long; only the reader's own **Retry** goes at once, because
 * a person pressing a button is not a runaway client.
 */
export const AUTOSAVE_RETRY_BACKOFF_MS = [2_000, 5_000, 15_000, 30_000] as const;

/**
 * How many times the 409 probe below is asked before the screen settles for the half of the sentence
 * it already knows. Bounded, because unlike a save it is not carrying anybody's work: it is choosing
 * between two explanations, and saying less is better than guessing.
 */
const REFUSAL_PROBE_ATTEMPTS = 3;

/**
 * **Which of the autosave's two 409s stopped it** — §6.6 specified neither, and TASK-083 designs both.
 *
 * ⚠️ **The API answers them with one shape, so the client cannot tell them apart from the refusal.**
 * `PATCH /api/v1/interviews/{id}/` returns `409 ErrorResponse` for *submitted already* (ADR 0008) and
 * for *the candidate's personal data was erased* (ADR 0009) — one `{detail}` body, two rules, and
 * `openapi.snapshot.json` declares a single schema for the status. Reading the server's sentence to
 * choose ours is out of the question twice over: §8 and §11 forbid rendering a `detail`, and matching
 * on its wording would couple this screen to a backend string that can be reworded without notice.
 *
 * So `unknown` is a first-class value here: it is what the screen says while it does not yet know, and
 * what it keeps saying if the one read below cannot answer. The shared half of the sentence is true in
 * all three cases, and only the refinement is withheld.
 */
export type AutosaveRefusal = 'submitted' | 'anonymized' | 'unknown';

/**
 * **Which refusal the probed record actually shows** — and it is read off the **erasure**, never inferred
 * from the absence of a submission (BUG-041).
 *
 * ⚠️ **`submitted_at === null ? 'anonymized' : 'submitted'` is what stood here, and it is wrong on an
 * ordinary combination.** An erasure can land on a candidate whose interview was submitted an hour
 * earlier — ADR 0009 outranks ADR 0008's immutability and clears comment text on submitted rows too — so
 * that record has `submitted_at` set *and* an erasure, and the reader was told *submitted in another tab*.
 * Both sentences are true in part; only one of them is the cause, and the wrong one sends somebody hunting
 * for a colleague's tab instead of understanding that a person exercised a deletion right. This is the one
 * place on the surface where that mistake is possible, because it is the one sentence that explains a 409.
 *
 * So the order below is the order of the *evidence*, not of the rules: the erasure is a **fact on the
 * payload** (`consideration.person_anonymized_at`, backend TASK-107 — the field §6.7's own notice reads),
 * `submitted_at` is another, and a record showing neither explains nothing and is answered by `unknown`.
 * *Not submitted* was never evidence of an erasure.
 *
 * The blinded shape carries no consideration at all, so it cannot rule an erasure out and may not claim
 * either sentence. That branch is unreachable for the reader of §6.6 — this is their **own** interview and
 * ADR 0006 blinds other people's — and it is `unknown` rather than an assumption for the day it is not.
 * `isReadable` is the client's one discriminator (`api/interviews.ts`) and nothing here casts.
 */
const refusalShownBy = (record: InterviewRead): AutosaveRefusal => {
  if (!isReadable(record)) return 'unknown';
  if (record.consideration.person_anonymized_at !== null) return 'anonymized';
  if (record.submitted_at !== null) return 'submitted';
  return 'unknown';
};

/**
 * **The save indicator's states — §6.6's four, plus the one the two 409s needed.**
 *
 * | `kind` | §6.6 |
 * |---|---|
 * | `clean` | nothing rendered: nothing has been typed, so there is nothing to reassure anybody about |
 * | `saving` | *Saving…* with a spinner |
 * | `saved` | *Saved 14:32* in mono, from `at` — **the response's `updated_at`**, never the client clock |
 * | `failed` | *Not saved — retrying* in `negative`, with a **Retry** |
 * | `offline` | *"Offline. Your answers are kept in this tab until the connection returns."*, with a **Retry** (BUG-047) |
 * | `stopped` | the two 409s: the autosave has stopped for good, and nothing typed is lost from the page |
 * | `refused` | a 422: **that body** cannot be saved by sending it again, and nothing typed is lost from the page (BUG-068) |
 *
 * ⚠️ **`clean` renders nothing rather than *Saved* with the payload's own `updated_at`.** The payload
 * does carry that timestamp and it would look reassuring, but on arrival it is a statement about a save
 * this reader did not make — often the moment the interview was scheduled. The indicator is the
 * reassurance ADR 0007 offers **instead of** a Save button, so it may only ever report something that
 * actually happened while they were here.
 *
 * ⚠️ **`refused` is a sixth state and not the sixth one reused, and the reason is one clause** (BUG-068).
 * `stopped`'s lead says *"this interview has stopped accepting changes"* — true of both 409s, which are
 * facts about the **record**, and false of a 422, which is a fact about **one body**. The record goes on
 * accepting everything else, so borrowing that sentence would send a reader looking for a submission in
 * another tab or an erasure that never happened, which is the mistake BUG-041 already cost this screen
 * once. What the two states **do** share is the half that matters most and is said in both: nothing typed
 * is lost from the page.
 */
export type SaveStatus =
  | { kind: 'clean' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: string }
  | { kind: 'failed' }
  | { kind: 'offline' }
  | { kind: 'stopped'; refusal: AutosaveRefusal }
  | { kind: 'refused' };

export interface Autosave {
  status: SaveStatus;
  /** §6.6's **Retry**, beside *Not saved — retrying*: the one retry that goes out immediately. */
  retryNow: () => void;
  /**
   * ⚠️ **Everything typed on the server *now*, awaited — what submission has to do before it freezes
   * anything** (TASK-084, and see the note on `useAutosave` for the loss it closes). Resolves `true` when
   * the server holds every value on screen, `false` when it does not: a save that failed, a connection
   * that is away, or an autosave a 409 has stopped.
   */
  flush: () => Promise<boolean>;
  /**
   * ⚠️ **Close the write path for good** — called on a successful submission, because the interview has
   * just become immutable (ADR 0008) and a `PATCH` after it is a request that can only be refused. It is
   * the same latch the two 409s set, reached deliberately instead of by a refusal.
   */
  stop: () => void;
  /** The one honest next step after a 409 — re-read the interview (for a submitted one, that is §6.7). */
  reread: () => void;
  /**
   * The router's own block, `blocked` only when leaving would lose something. See the navigation note
   * on `useAutosave` for why an all-purpose *"unsaved changes"* prompt is refused here.
   */
  blocker: Blocker;
}

interface Options {
  /** The reader's own unsubmitted interview, as the server currently holds it. */
  interview: InterviewOut;
  /** The viewer, because the interview's cache entry is keyed on them (ADR 0006). */
  viewerId: number;
  /** §6.6's form. Watched through a subscription, so a keystroke re-renders nothing here. */
  form: UseFormReturn<FillingInForm>;
}

/** A body with nothing in it is not a save — `draftChanges` returns `{}` when nothing differs. */
const nothingToSave = (changes: InterviewDraft): boolean => Object.keys(changes).length === 0;

/**
 * §6.6's autosave.
 *
 * ⚠️ **Saves are serialised, never overlapped.** One request is in flight at a time, and a change made
 * while one is in flight is **queued and sent after it, carrying the later value**. The precedent is
 * Sprint 10's reorder race, where a second write built its body from a state the server had already
 * superseded: here the body is a diff against the **last successful save**, so two overlapping requests
 * would each diff against the same stale baseline and the second could re-assert a value the first had
 * already replaced. The queue is what makes that unrepresentable — `inFlight` refuses a second start,
 * and the success path re-schedules through `arm` if anything has changed since the body went out (it
 * re-ran *immediately* until BUG-030, which turned the round trip into the save cadence; re-arming the
 * bare debounce, which was that bug's first fix, then meant a burst of typing saved nothing — see `arm`
 * for both halves and `AUTOSAVE_MAX_WAIT_MS` for the bound that closes the second).
 *
 * ⚠️ **The baseline advances to what was *sent*, not to what came back.** On a 200 the last-saved
 * fields become the exact snapshot the body was built from. Taking them from the response instead would
 * look more authoritative and would be worse: another tab's save is in that payload, and this form
 * would then diff against it and re-send its own older text over the top — the reorder race again, one
 * layer down. A field nobody on this screen touched is never in a body from this screen.
 *
 * ⚠️ **Navigation is decided here rather than defaulted.** With everything saved there is **no**
 * *"unsaved changes"* prompt: that prompt is the anxiety autosave was built to remove, and a screen
 * that saves every two seconds and then asks whether you meant it teaches the same lesson a Save button
 * does. What is blocked is the case where leaving really does lose something — a failed save, or one
 * queued behind a dropped connection — and the same condition arms a `beforeunload`, because ADR 0007's
 * named failure is the tab that closes and never opens again. ⚠️ **An autosave a 409 stopped is not one of
 * those cases**, and it was treated as one until BUG-026 — see `atRisk`, where the whole argument is.
 *
 * ⚠️ **What covers the window a scheduled save leaves open, path by path — because naming the wrong
 * mitigation was one of BUG-030's findings.** An in-app **navigation** unmounts this hook and the flush on
 * unmount sends what the timer was still holding. A **submission** awaits `flush()`. A **tab close** does
 * neither: nothing unmounts, and the `beforeunload` above is armed only in `failed`/`offline`, which are
 * precisely the states a normally-saving screen is not in. So that one window is bounded by
 * `AUTOSAVE_MAX_WAIT_MS` and by nothing else, which is why the bound exists and why it is short.
 */
export function useAutosave({ interview, viewerId, form }: Options): Autosave {
  const queryClient = useQueryClient();
  const interviewId = interview.id;

  const [status, setStatus] = useState<SaveStatus>({ kind: 'clean' });

  /**
   * ⚠️ **The mutation goes through React Query, and that is what signs the app out on a 401.** The
   * rule lives in one place — `queryClient.ts`'s mutation cache, whose docblock predicted this task in
   * these words: *"the next sprint's draft autosave is a `PATCH` (ADR 0007), and an interviewer typing
   * into a dead session is the exact loss that ADR exists to prevent"*. Calling `saveInterviewDraft`
   * directly would work and would quietly opt out of it, leaving a screen that types into a session
   * that ended. There is no `retry` here either: the app default is `mutations: { retry: false }`, so
   * every retry on this screen is the one below, deliberate and asserted.
   *
   * ⚠️ **`networkMode: 'always'` — the one line that made §6.6's Offline row reachable at all
   * (BUG-047, 2026-08-09).** React Query's default is `'online'`, and what that setting does is not
   * what its name suggests: while `onlineManager` reports the browser offline, `mutateAsync`
   * **pauses** — no request is made, the promise never settles, `run` never reaches its `catch`, and
   * this hook's own `offline` branch is therefore dead code. Measured in a live browser: with
   * `navigator.onLine === false` the indicator sat on *Saving…* for 25 s with no request on the wire,
   * and every state that hangs off it was silent with it — no `negative` line, no Retry, no `atRisk`,
   * so neither the router block nor `beforeunload` was armed either. The **work** was safe (the
   * paused mutation resumed on reconnection and the `PATCH` landed), which is why this was a medium
   * and not the loss ADR 0007 exists for: what was missing was the *message*.
   *
   * ⚠️ **Pausing is the wrong tool here specifically because this screen has a queue of its own.**
   * React Query's pause is a queue nobody can see: it has no state to render, no Retry to press and
   * no bound anyone stated, and it swallows `flush()` too — a **Submit** pressed offline awaited a
   * promise that could not settle. What §6.6 asks for instead is an attempt that **fails**, so the
   * failure can be shown in words the reader can act on and the retry ladder below can own the
   * cadence (capped at 30 s, i.e. 2/min, which is what the ladder's own docblock already claims). The
   * request costs nothing when the browser refuses it: it fails locally and instantly, without
   * reaching a network.
   */
  const { mutateAsync } = useMutation({
    networkMode: 'always',
    mutationFn: (changes: InterviewDraft) => saveInterviewDraft(interviewId, changes),
  });

  /** What the server holds, as far as this screen knows: the payload at mount, then each sent body. */
  const saved = useRef<DraftFields>(draftFieldsOf(interview));
  /** What is on screen. Replaced on every change, and read only when a body is built. */
  const current = useRef<DraftFields>(saved.current);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(false);
  /**
   * The request in the air, as a promise — so `flush` can **wait for** a save rather than race it.
   * `inFlight` alone says *that* one is open and gives nothing to await, which on the submission path is
   * the difference between freezing what is on screen and freezing what the server happened to hold.
   */
  const flight = useRef<Promise<void> | null>(null);
  /**
   * ⚠️ **When the oldest edit the server does not have was made — the anchor of the maximum wait, and the
   * whole state the second half of BUG-030's fix needs.** It is set the first time a save is scheduled with
   * nothing already pending, cleared the moment a body carrying that edit goes on the wire, and set again
   * by the next edit after it. So the deadline is measured from **the reader's own keystroke** rather than
   * from the last save or from mount: typing that starts after a quiet hour is bounded from the first
   * character of it, and a screen nobody is typing into schedules nothing at all.
   */
  const pendingSince = useRef<number | null>(null);
  const attemptsMade = useRef(0);
  /** A 409 is final: the autosave stops and nothing restarts it but a re-read of the screen. */
  const stopped = useRef(false);
  /** The engine, reached through a ref so a scheduled retry always runs the current closure. */
  const attemptRef = useRef<() => void>(() => {});

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  /**
   * ⚠️ **The one way a save is scheduled, and it is a debounce *with a maximum wait*** — BUG-030 needed
   * both halves and its first attempt shipped one.
   *
   * **The first half — one scheduler, because there used to be two.** A keystroke armed the debounce; the
   * **queued edit** — anything typed while a save was in flight — did not, it re-entered the engine
   * straight from the success handler. So from the first mid-flight keystroke onwards every flight carried
   * one, the cadence became the **round-trip time**, and a 60 ms round trip put sustained typing at ~1000
   * requests a minute against a throttle of 300 that bounds the reader's whole authenticated API: their own
   * dashboard and candidate list throttled by typing. Nothing was lost on that engine — the diff survives,
   * the next success carries it, and the server was never more than one round trip behind the screen — which
   * is why it was invisible for two sprints while three documents stated 30/min.
   *
   * **The second half — the deadline, because a trailing debounce never elapses while somebody is still
   * typing.** With re-arming alone, an uninterrupted burst sent nothing at all: 193 characters over 22 s of
   * gap-free typing produced **zero** PATCHes in a live browser, and the tab that closed lost all of them.
   * So every arming schedules for `min(AUTOSAVE_DEBOUNCE_MS, deadline - now)`, where the deadline is
   * `pendingSince + AUTOSAVE_MAX_WAIT_MS` — the pause if it comes, the deadline if it does not. Typing that
   * never stops therefore saves once per maximum wait instead of never, and because that wait is the longer
   * of the two intervals it can only *lower* the cadence, never raise it: the rate ceiling is still the
   * debounce's.
   *
   * Every path that reacts to *typing* comes through here — the form subscription and the queued edit — so
   * both bounds hold by construction rather than by intention. The three that bypass it are each an **event
   * and not a rate**, which is why they may: the reader's own **Retry**, the browser's `online`, and
   * `flush()` before a submission (one request per act, in every case). It clears first, so a keystroke and
   * a landing save cannot leave two timers behind for one body.
   */
  const arm = useCallback(() => {
    clearTimer();

    const now = Date.now();
    if (pendingSince.current === null) pendingSince.current = now;

    // `Math.max(0, …)` rather than a negative delay: a deadline already passed fires on the next tick, and
    // a clock that jumps backwards (an NTP correction mid-interview) cannot postpone a save indefinitely.
    const untilDeadline = pendingSince.current + AUTOSAVE_MAX_WAIT_MS - now;
    const wait = Math.max(0, Math.min(AUTOSAVE_DEBOUNCE_MS, untilDeadline));

    timer.current = setTimeout(() => attemptRef.current(), wait);
  }, [clearTimer]);

  /**
   * **The one read that tells the two 409s apart** — see `AutosaveRefusal` for why the refusal itself cannot.
   *
   * ⚠️ **It deliberately does not go through the interview's query.** `fetchQuery` would write this
   * payload into the cache the screen renders from, `index.tsx` would branch on the new `submitted_at`,
   * and §6.7 would replace the form — taking with it every word the reader has on screen, which is the
   * one thing both 409s must not do. So this is a bare read whose entire result is **one of three words**:
   * nothing is stored, nothing is rendered from it, and the screen keeps every typed value.
   *
   * **Which of the three answers the payload supports is `refusalShownBy`'s** — two fields are read, the
   * erasure timestamp and `submitted_at`, and nothing else on the record is looked at, stored or rendered.
   */
  const probeRefusal = useCallback(
    async (attemptsLeft: number = REFUSAL_PROBE_ATTEMPTS): Promise<void> => {
      try {
        const record = await fetchInterview(interviewId);
        setStatus({ kind: 'stopped', refusal: refusalShownBy(record) });
      } catch {
        // It answers or it does not; either way the screen has already said the true half. A second
        // failure changes nothing about what is safe to claim, so it is asked again and then dropped.
        if (attemptsLeft > 1) {
          timer.current = setTimeout(
            () => void probeRefusal(attemptsLeft - 1),
            AUTOSAVE_RETRY_BACKOFF_MS[0],
          );
        }
      }
    },
    [interviewId],
  );

  /**
   * One save: the guards, the body, and the request — and it **returns the request's own promise**, which
   * is what `flush` awaits. A call that starts nothing returns the flight already in the air (or a
   * resolved promise), so a caller can always await *"whatever is happening now"* without knowing which
   * case it got.
   */
  const attempt = useCallback((): Promise<void> => {
    clearTimer();
    if (stopped.current) return Promise.resolve();
    if (inFlight.current) return flight.current ?? Promise.resolve();

    /*
     * The snapshot the body is built from, kept so the baseline can advance to exactly what was sent.
     * Everything typed after this line is a later value and belongs to the next save.
     */
    const sending = current.current;
    const changes = draftChanges(saved.current, sending);
    if (nothingToSave(changes)) {
      // Typed and taken away again: there is nothing behind schedule either, so the deadline is released
      // rather than left anchored to a keystroke no body will ever carry.
      pendingSince.current = null;
      return Promise.resolve();
    }

    inFlight.current = true;
    /*
     * ⚠️ **A save started while the last one stands refused does not blank the notice** (BUG-068). The
     * refused body is only ever re-sent under a **later** edit, so this transition would otherwise fire
     * once per pause in the typing — unmounting a `role="alert"` block and mounting it again with the
     * same words each time, which a screen reader reads out as a new announcement every two seconds.
     * Returning the same object also bails React out of the render entirely. Every other transition out
     * of `refused` is unaffected: a 200 says *Saved*, a dropped connection says *Offline*.
     */
    setStatus((showing) => (showing.kind === 'refused' ? showing : { kind: 'saving' }));
    /*
     * ⚠️ **The deadline is discharged here, where the body leaves — not on the answer.** Everything typed up
     * to `sending` is on the wire now, so nothing is behind schedule; anything typed from the next line
     * onwards is a *new* pending edit and anchors its own deadline in `arm`. Clearing it on the **success**
     * instead would let a slow round trip carry the old anchor over an edit made during it, and the second
     * save would then be scheduled against a deadline that had already passed — a save per round trip,
     * which is the flood BUG-030 opened with.
     */
    pendingSince.current = null;

    const run = async (): Promise<void> => {
      try {
        const answer = await mutateAsync(changes);
        saved.current = sending;
        attemptsMade.current = 0;
        inFlight.current = false;
        // §6.6's *Saved 14:32* is the **server's** save timestamp and never this machine's clock.
        setStatus({ kind: 'saved', at: answer.updated_at });
        /*
         * The interview's own cache entry now holds what the server holds. It is not read to advance the
         * baseline (see the note on this hook); it is written so that leaving the screen and coming back
         * inside `staleTime` cannot render a form older than the draft on the server.
         */
        queryClient.setQueryData(queryKeys.interview(viewerId, interviewId), answer);

        /*
         * ⚠️ **The queued edit — anything typed while that request was in flight — is scheduled like every
         * other edit: the pause, or the maximum wait if no pause comes** (BUG-030, and see `arm`). It went
         * out *immediately* here until 2026-08-03, which made the round trip the cadence; it then waited for
         * a pause and nothing else, which is what made a burst of typing save nothing at all. The value it
         * carries is the later one in every version, because the body is built when the timer fires and not
         * now.
         *
         * ⚠️ **The window this leaves open, stated as what it is.** An edit is on the wire at the latest
         * `AUTOSAVE_MAX_WAIT_MS` after it was typed, plus the tail of a save already in flight (this
         * branch), plus its own round trip — of the order of **ten seconds** on a working connection. Close
         * the tab inside it and that much is lost, and the two mitigations named here before are **not** the
         * ones that cover it: nothing unmounts on a tab close, so the flush on unmount does not run, and
         * `beforeunload` is armed only in `failed`/`offline` and so is silent in exactly this state. What
         * bounds it is the maximum wait itself. The unmount flush covers an in-app **navigation**, and
         * `flush()` covers a **submission**; a tab close is covered by the bound alone, which is why the
         * bound exists.
         */
        if (!nothingToSave(draftChanges(saved.current, current.current))) arm();
        return;
      } catch (failure) {
        inFlight.current = false;

        /*
         * ⚠️ **The maximum wait's anchor is deliberately *not* restored here, and that is a rate decision.**
         * The body did fail, so the server is still behind the screen — but re-anchoring it to the keystroke
         * this request was built for would leave a deadline already in the past, and then **every further
         * keystroke would schedule a save for the next tick**: four requests a second at an endpoint already
         * answering 500 or nothing, which is a worse flood than the one BUG-030 opened with, and for a 429 it
         * is the amplification `queryClient.ts` forbids by name. A failure is the retry ladder's regime, not
         * the deadline's: the ladder below resends everything, the indicator says *Not saved — retrying* so
         * the reader is not being told a comfortable lie, and the next edit anchors a fresh deadline.
         */
        const error = failure instanceof ApiError ? failure : new ApiError(0);

        /*
         * ⚠️ **A 409 stops the autosave for good.** Both of them are about the record rather than the
         * request: a submitted interview is immutable (ADR 0008) and an anonymised candidate is closed to
         * new activity (ADR 0009), so a retry could only be refused again. Nothing typed is cleared — the
         * form is untouched and the notice says so.
         */
        if (error.status === 409) {
          stopped.current = true;
          clearTimer();
          setStatus({ kind: 'stopped', refusal: 'unknown' });
          void probeRefusal();
          return;
        }

        /*
         * ⚠️ **A 401 is the session ending, and the app is already signing out** — the mutation cache
         * invalidated the session query before this line ran, `RequireSession` sees it fail and this
         * screen is replaced by sign-in with the address kept. So there is nothing to retry and nothing
         * this screen may claim: `stopped` with no refinement says the true half — the autosave has
         * stopped and what is on screen is still on screen — and it never says *retrying* about a request
         * that will not be retried, which is BUG-017's shape.
         */
        if (error.isUnauthenticated) {
          stopped.current = true;
          clearTimer();
          setStatus({ kind: 'stopped', refusal: 'unknown' });
          return;
        }

        /*
         * ⚠️ **A 422 is refused on its *content*, so the same body is never sent again** (BUG-068). It
         * fell through to the ladder below until 2026-08-09 and therefore said *"Not saved — retrying"*
         * and retried until the tab closed — a promise nothing could keep, which is BUG-017's shape with
         * the retry real and the success impossible. Nothing on the page is touched: the text is all
         * still there and the notice says so.
         *
         * ⚠️ **It does not latch `stopped`, and that is the decision rather than an omission.** The two
         * 409s and the 401 are facts about the **record** or the **session**, so nothing a reader does
         * can make the next request succeed and the write path is closed for good. A 422 is a fact about
         * **one body**: a later edit builds a different one, and if it no longer carries what was
         * refused it saves. Latching would mean a single refused value silently ends protection #4 for
         * the rest of the sitting — every other question's comment typed after it never reaching the
         * server — which is ADR 0007's own named failure caused by this client. So the ladder stops and
         * the write path stays open; the next edit re-arms through `arm` like any other, at the
         * debounce's cadence and no faster, and no timer here ever re-sends the body that was refused.
         *
         * The reachable 422s today are structural — a repeated `question_id`, a rating out of range, a
         * `question_id` outside the interview — so this branch reads the **status** and nothing else. It
         * neither renders the server's sentence nor matches on it (§8, §11), and the field list of
         * `SubmissionRefused` is the submission's to place at fields (`submissionProblems.ts`); a
         * refusal of the *draft* names fields this screen has no message for.
         */
        if (error.status === 422) {
          setStatus({ kind: 'refused' });
          return;
        }

        /*
         * Everything else is retried, and never in the same tick: a 429 above all (`queryClient.ts`'s own
         * rule for BUG-005/BUG-011 — the client that caused a throttle must not amplify it), a 5xx, and a
         * connection that answered nothing at all. The delay grows and then stops growing.
         */
        setStatus(error.isOffline ? { kind: 'offline' } : { kind: 'failed' });
        const delay =
          AUTOSAVE_RETRY_BACKOFF_MS[
            Math.min(attemptsMade.current, AUTOSAVE_RETRY_BACKOFF_MS.length - 1)
          ];
        attemptsMade.current += 1;
        timer.current = setTimeout(() => attemptRef.current(), delay);
      }
    };

    const started = run();
    flight.current = started;
    return started;
  }, [arm, clearTimer, interviewId, mutateAsync, probeRefusal, queryClient, viewerId]);

  useEffect(() => {
    attemptRef.current = () => void attempt();
  }, [attempt]);

  /**
   * ⚠️ **The form is *subscribed* to, not watched into state.** `form.watch(callback)` notifies without
   * re-rendering, so a keystroke on the longest form in the product costs one ref assignment and one
   * timer — the property TASK-082 built `QuestionBlock` and `OverallBlock` around, and the reason the
   * status below is the only thing here that renders.
   */
  useEffect(() => {
    /*
     * The values are read back with `getValues()` rather than taken from the callback's argument, which
     * `react-hook-form` types as a `DeepPartial` — and a cast is the one thing this layer must not do
     * with a shape (`api/interviews.test.tsx` walks `src/` for exactly that habit). `getValues()` is the
     * whole form, typed as the whole form.
     */
    const subscription = form.watch(() => {
      current.current = draftFieldsOfForm(form.getValues());
      if (stopped.current) return;

      arm();
    });

    return () => subscription.unsubscribe();
  }, [arm, form]);

  /**
   * ⚠️ **The reconnection is an event, not a hope.** §6.6's offline line promises the answers survive
   * in this tab while the connection is away, so the moment the browser says it is back the pending body
   * goes out — the backoff is what covers a connection that comes back without the event (a captive
   * portal, a proxy that answers nothing), and the two together are why that sentence is true here and
   * was false on §8's shared banner (BUG-017).
   */
  useEffect(() => {
    const reconnected = () => {
      if (stopped.current) return;
      clearTimer();
      attemptsMade.current = 0;
      attemptRef.current();
    };

    window.addEventListener('online', reconnected);
    return () => window.removeEventListener('online', reconnected);
  }, [clearTimer]);

  /**
   * ⚠️ **Leaving flushes what the debounce is still holding.** Without this, navigating away one second
   * after a keystroke would drop that edit in silence — and since a saved screen deliberately raises no
   * prompt, silence is all there would be. The request is sent directly rather than through the
   * mutation, because the component is going away and a mutation observer that unmounts mid-flight is a
   * worse guarantee than a plain promise; what it cannot do is report a failure to a screen that no
   * longer exists, and that residual is recorded rather than hidden (the case that *can* lose work —
   * a save already known to have failed — is the one the router block stops).
   *
   * ⚠️ **This runs on an unmount and a tab close is not one** — no React cleanup runs when a tab is closed,
   * and `beforeunload` is armed only in `failed`/`offline`. So this covers leaving the **screen**, not
   * leaving the **browser**; the bound on the latter is `AUTOSAVE_MAX_WAIT_MS` (BUG-030, whose first attempt
   * named this effect as the mitigation for exactly the case it cannot reach).
   */
  useEffect(
    () => () => {
      clearTimer();
      if (stopped.current) return;

      const changes = draftChanges(saved.current, current.current);
      if (nothingToSave(changes)) return;
      void saveInterviewDraft(interviewId, changes).catch(() => {});
    },
    [clearTimer, interviewId],
  );

  /**
   * ⚠️ **What is at risk, and it is deliberately not "the form is dirty".** A dirty form whose last
   * save succeeded is on the server; warning about it would be the *"unsaved changes"* prompt §6.6
   * refuses. What is at risk is a save that **failed** or one **queued** behind a dropped connection —
   * work that can still reach the server, where staying on the page is what gets it there.
   *
   * ⚠️ **`stopped` was in this list until BUG-026, and it made the screen a trap.** A 409 armed the
   * router block, and `SaveNotice` returns the *stopped* block before it ever reaches the *Leave anyway*
   * prompt — the only control in the product that can call `blocker.proceed()`. So every in-app link did
   * nothing, in all three situations that reach the state: submitted in another tab (ADR 0008), the
   * candidate erased mid-draft (ADR 0009), and `unknown`, which is also the **401 sign-out** path below.
   * `anonymized` and `unknown` had no way out at all short of a reload, which then met the `beforeunload`
   * warning as well.
   *
   * ⚠️ **`refused` is out of this list too, and for the same test rather than by analogy** (BUG-068).
   * What the two warnings offer is *stay, and it will reach the server* — which is true of a failed save
   * and of a queued one, because **waiting** is what lands those. A refused body is not landed by
   * waiting: only an edit changes it, and the notice asks for one in words. Arming a prompt a reader
   * cannot satisfy by staying is the trap BUG-026 recorded, and the sentence they are owed instead —
   * *"copy anything you need before you leave it"* — is in the block itself.
   *
   * It is out because **the blocking protected nothing**: after a 409 the record has stopped accepting
   * changes, so there is nothing on this page that staying could save, and both warnings — the router's
   * and the browser's — say the opposite by implication. `InterviewShell`'s own rule is that §8's states
   * are not dead ends. What the reader is owed here is the sentence the notice already renders, *"copy
   * anything you need before you leave it"*, and then the door; `DESIGN.md` §6.6's navigation bullet is
   * corrected to match, since it listed the 409 with the two that really are at risk.
   */
  const atRisk = status.kind === 'failed' || status.kind === 'offline';

  const blocker = useBlocker(atRisk);

  useEffect(() => {
    if (!atRisk) return;

    // The browser's own prompt, whose wording is the browser's — this only says there is something to
    // lose. ADR 0007's named failure is the tab that closes and is never opened again.
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [atRisk]);

  const retryNow = useCallback(() => {
    clearTimer();
    attemptsMade.current = 0;
    attemptRef.current();
  }, [clearTimer]);

  const reread = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.interview(viewerId, interviewId) });
  }, [interviewId, queryClient, viewerId]);

  /**
   * ⚠️ **Everything on screen on the server before anything is frozen — the point where protections #4
   * and #5 actually meet** (TASK-084). Submission sends **no body** (ADR 0008: it is an act, not a
   * write), so what it freezes is whatever the last `PATCH` left on the server — and the scheduler means
   * that is up to `AUTOSAVE_MAX_WAIT_MS` behind the screen (the debounce's two seconds when there was a
   * pause, the maximum wait when there was not). Submitting straight after a keystroke would therefore
   * freeze a *stale* interview, silently and irreversibly, which is ADR 0007's own failure arriving
   * through the one door that cannot be reopened. So the caller awaits this first, and submits only on
   * `true`.
   *
   * Three awaits, and each is a case: the request already in the air, the body the debounce was still
   * holding, and the queued round the success path starts when something was typed mid-flight. Nothing
   * here rejects — `run` handles every failure itself — so the answer is read off the diff rather than
   * off an exception: `true` means the server holds every value on screen.
   */
  const flush = useCallback(async (): Promise<boolean> => {
    clearTimer();
    if (stopped.current) return false;

    await flight.current;
    await attempt();
    await flight.current;

    return nothingToSave(draftChanges(saved.current, current.current));
  }, [attempt, clearTimer]);

  const stop = useCallback(() => {
    stopped.current = true;
    clearTimer();
  }, [clearTimer]);

  return { status, retryNow, flush, stop, reread, blocker };
}
